'use client'

import { useEffect, useId, useRef, type ReactNode } from 'react'
import { useTranslations } from 'next-intl'

import { ArrowLeft } from 'lucide-react'

import type { MoodSectionData } from '@/lib/blog/moods'
import { MoodPostGrid } from '@/components/blog/mood-post-grid'

import { backToBlog, useActiveMoodSlug } from './mood-filter'

interface BlogHomeContentProps {
    /** The full blog homepage (all sections, incl. the mood tiles). */
    children: ReactNode
    /** Featured + non-featured moods with their post listings — resolved server-side. */
    moods: MoodSectionData
}

/**
 * The blog home body switch (blog-moods FR-8, RD-15): with no `?mood=` param the
 * full homepage (`children`) renders; when a mood is active the homepage is
 * **cleared** and only that mood's post listing shows — a "← Back to blog" link,
 * the mood label, and the {@link MoodPostGrid} (or a friendly empty state).
 *
 * The active mood is read from the URL (`useActiveMoodSlug`) whose server
 * snapshot is `null`, so the server HTML is always the full homepage regardless
 * of `?mood=` — keeping `/blog` a single static, canonical surface (no per-mood
 * page is indexed). Selection is driven by `history.pushState` (see
 * `./mood-filter`), so the browser Back button returns to the homepage.
 */
export const BlogHomeContent = ({ children, moods }: BlogHomeContentProps) => {
    const t = useTranslations('blogHome.wanderByMood')
    const activeSlug = useActiveMoodSlug()
    const headingId = useId()
    const headingRef = useRef<HTMLHeadingElement | null>(null)

    const active = activeSlug
        ? ([...moods.featured, ...moods.surprisePool].find((mood) => mood.slug === activeSlug) ??
          null)
        : null

    // The mood view replaces the whole homepage like a client-side route change;
    // move focus to its heading so keyboard/SR users land in — and hear — the new
    // context instead of being stranded on <body>. Re-runs per opened mood.
    useEffect(() => {
        if (active) {
            headingRef.current?.focus()
        }
    }, [active?.slug])

    if (!active) {
        return <>{children}</>
    }

    return (
        <section
            aria-labelledby={headingId}
            className="mx-auto w-full max-w-7xl px-4 py-10 md:px-10 md:py-14"
        >
            <button
                type="button"
                onClick={backToBlog}
                className="inline-flex items-center gap-2 text-[15px] font-medium text-text-secondary hover:text-text-primary"
            >
                <ArrowLeft aria-hidden="true" className="size-4" />
                {t('back')}
            </button>
            <h1
                id={headingId}
                ref={headingRef}
                tabIndex={-1}
                className="mt-4 mb-8 text-3xl font-bold tracking-tight text-text-primary outline-none md:text-4xl"
            >
                {active.label}
            </h1>
            {active.posts.length === 0 ? (
                <p className="text-text-secondary">{t('empty')}</p>
            ) : (
                <MoodPostGrid key={active.slug} posts={active.posts} />
            )}
        </section>
    )
}
